'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { smartTruncate } from '@/shared/text';
import { externalLinkProps } from '@/shared/links';
import { generateGoogleCalendarUrl, generateOffice365CalendarUrl } from '@/shared/calendar';
import iconOpenInNew from 'assets/icons/open_in_new.svg';
import iconLocation from 'assets/icons/location_on.svg';
import Styles from '@/assets/css/ckeditorEUI.module.css';
import UIButton from './UIButtons/UIButton';
export default function HeroSection({
  title,
  locationName,
  description,
  directionsUrl,
  detailsUrl,
  className = '',
  titleHideOnMobile = true,
  enableExpandDescription = true,
  timeRows = [],
  isLocationClickableLink = false
}) {
  const [expanded, setExpanded] = React.useState(false);
  const {
    short,
    isTruncated
  } = React.useMemo(() => smartTruncate(description, 400), [description]);

  // Helper to validate URL
  const isValidUrl = url => {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    // Check if URL is more than just the protocol
    if (trimmed === 'http://' || trimmed === 'https://' || trimmed.length < 10) return false;
    try {
      const parsedUrl = new URL(trimmed);
      return parsedUrl.hostname && parsedUrl.hostname.includes('.');
    } catch {
      return false;
    }
  };

  // Calendar modal state
  const [selectedTimeRow, setSelectedTimeRow] = React.useState(null);
  const calendarModalRef = React.useRef(null);

  // Close modal when clicking outside
  React.useEffect(() => {
    const handleClickOutside = event => {
      if (calendarModalRef.current && !calendarModalRef.current.contains(event.target) && !event.target.closest('button[aria-label*="Add"]')) {
        setSelectedTimeRow(null);
      }
    };
    if (selectedTimeRow !== null) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [selectedTimeRow]);

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEscape = event_0 => {
      if (event_0.key === 'Escape') {
        setSelectedTimeRow(null);
      }
    };
    if (selectedTimeRow !== null) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedTimeRow]);

  // Generate calendar URLs
  const handleCalendarClick = (row, calendarType) => {
    if (!row.dateTime || !row.endDateTime) return;
    const calendarUrl = calendarType === 'google' ? generateGoogleCalendarUrl({
      title: title || '',
      start: row.dateTime,
      end: row.endDateTime,
      description: description || '',
      location: locationName || ''
    }) : generateOffice365CalendarUrl({
      title: title || '',
      start: row.dateTime,
      end: row.endDateTime,
      description: description || '',
      location: locationName || ''
    });
    window.open(calendarUrl, '_blank', 'noopener,noreferrer');
    setSelectedTimeRow(null);
  };
  return <div className={`${className} pt-5 lg:pt-0`}>
      <div className="lg:max-w-[27.5em] w-full">
        <h1 className={`${titleHideOnMobile ? 'hidden' : ''}  lg:block lg:text-[#323232] mb-5 font-outfit 2xl:text-[3.4em] xl:text-[2.8em] lg:text-[2.4em] md:text-[2em] text-[1.6em] font-semibold leading-[1em]`}>
          {title}
        </h1>
        <div className={`2xl:mb-10 xl:mb-8 lg:mb-5 mb-4 ${titleHideOnMobile ? 'hidden!' : ''}`}>
          {isLocationClickableLink && isValidUrl(directionsUrl) ? <Link className={`flex gap-2 items-start text-gray-500 lg:mb-2 mb-1.5 ${titleHideOnMobile ? 'hidden!' : ''}`} href={directionsUrl} target="_blank" rel="noopener noreferrer">
              <span className="lg:-mt-0.5 -mt-px inline-flex lg:h-5 h-4 lg:w-5 w-4 items-center justify-center text-[#00EAFD]" aria-hidden>
                <svg viewBox="0 0 24 24" className="h-4 w-auto">
                  <path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />
                </svg>
              </span>
              <span className="text-[#626262] font-outfit lg:text-sm text-[0.65em] leading-[1.28em] font-normal">
                {locationName}
              </span>
            </Link> : <div className={`flex gap-2 text-gray-500 ${titleHideOnMobile ? 'hidden' : ''}`}>
              <span className="lg:-mt-0.5 inline-flex lg:h-5 h-4 lg:w-5 w-4 items-center justify-center text-[#00EAFD]" aria-hidden>
                <svg viewBox="0 0 24 24" className="h-4.5 w-auto">
                  <path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />
                </svg>
              </span>
              <span className="text-[#626262] font-outfit lg:text-sm text-[0.65em] leading-[1.28em] font-normal">
                {locationName}
              </span>
            </div>}

          {timeRows.length > 0 && <div className="flex items-start gap-2 text-gray-500">
              <span className="mt-0.25 inline-flex lg:h-5 h-4 lg:w-5 w-4 *:items-center justify-center text-sky-500" aria-hidden>
                <svg viewBox="0 0 17 16" className="h-3.5 w-3.5" fill="none">
                  <path d="M8.79333 7.91521V4.62667C8.79333 4.44931 8.73347 4.30083 8.61375 4.18125C8.49417 4.06153 8.3457 4.00167 8.16833 4.00167C7.99097 4.00167 7.8425 4.06153 7.72292 4.18125C7.6032 4.30083 7.54333 4.44931 7.54333 4.62667V8.1075C7.54333 8.20792 7.56153 8.30354 7.59792 8.39438C7.63417 8.48507 7.69132 8.56945 7.76937 8.6475L10.2069 11.085C10.3222 11.2004 10.4672 11.2594 10.6419 11.2621C10.8166 11.2647 10.9643 11.2057 11.085 11.085C11.2057 10.9643 11.266 10.8179 11.266 10.6458C11.266 10.4739 11.2057 10.3276 11.085 10.2069L8.79333 7.91521ZM8.16833 15.2517C7.18542 15.2517 6.26451 15.0658 5.40563 14.694C4.5466 14.3222 3.79764 13.8168 3.15875 13.1779C2.51986 12.539 2.01451 11.7901 1.64271 10.931C1.2709 10.0722 1.085 9.15125 1.085 8.16833C1.085 7.18542 1.2709 6.26451 1.64271 5.40563C2.01451 4.5466 2.51986 3.79764 3.15875 3.15875C3.79764 2.51986 4.5466 2.01451 5.40563 1.64271C6.26451 1.2709 7.18542 1.085 8.16833 1.085C9.15125 1.085 10.0722 1.2709 10.931 1.64271C11.7901 2.01451 12.539 2.51986 13.1779 3.15875C13.8168 3.79764 14.3222 4.5466 14.694 5.40563C15.0658 6.26451 15.2517 7.18542 15.2517 8.16833C15.2517 9.15125 15.0658 10.0722 14.694 10.931C14.3222 11.7901 13.8168 12.539 13.1779 13.1779C12.539 13.8168 11.7901 14.3222 10.931 14.694C10.0722 15.0658 9.15125 15.2517 8.16833 15.2517ZM0.18125 3.40229C0.0604168 3.2816 0 3.13521 0 2.96313C0 2.79118 0.0604168 2.64486 0.18125 2.52417L2.52417 0.18125C2.63958 0.0658336 2.78458 0.00680581 2.95917 0.00416692C3.13389 0.00138914 3.2816 0.0604169 3.40229 0.18125C3.52299 0.301945 3.58333 0.448264 3.58333 0.620209C3.58333 0.792292 3.52299 0.938681 3.40229 1.05938L1.05938 3.40229C0.943958 3.51771 0.798958 3.57674 0.624375 3.57938C0.449653 3.58201 0.301945 3.52299 0.18125 3.40229ZM16.1554 3.40229C16.0347 3.52299 15.8884 3.58333 15.7165 3.58333C15.5444 3.58333 15.398 3.52299 15.2773 3.40229L12.9344 1.05938C12.819 0.943959 12.7599 0.798959 12.7573 0.624376C12.7547 0.449654 12.8137 0.301945 12.9344 0.18125C13.0551 0.0604169 13.2015 0 13.3735 0C13.5455 0 13.6918 0.0604169 13.8125 0.18125L16.1554 2.52417C16.2708 2.63958 16.3299 2.78458 16.3325 2.95917C16.3353 3.13389 16.2763 3.2816 16.1554 3.40229Z" fill="#00EAFD" />
                </svg>
              </span>
              <div className="space-y-0.5 relative">
                {timeRows.map((row_0, index) => <div key={row_0.dateTime || index} className="relative">
                    <button type="button" onClick={() => setSelectedTimeRow(index)} className="block cursor-pointer text-[#626262] font-outfit lg:text-sm text-[0.65em] leading-[1.28em] font-normal" aria-label={`Add ${row_0.label}${row_0.timeText ? `, ${row_0.timeText}` : ''} to calendar`}>
                      <time dateTime={row_0.dateTime}>
                        <span>{row_0.label}</span>
                        {row_0.timeText && <span>{`, ${row_0.timeText}`}</span>}
                      </time>
                    </button>

                    {/* Calendar selection modal */}
                    {selectedTimeRow === index && <div ref={calendarModalRef} className="absolute left-0 top-full z-50 mt-2 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" role="dialog" aria-modal="true" aria-labelledby="calendar-modal-title">
                        <div className="p-2">
                          <h3 id="calendar-modal-title" className="px-3 py-2 text-sm font-semibold text-gray-900">
                            Add to Calendar
                          </h3>
                          <div className="space-y-1">
                            <button type="button" onClick={() => handleCalendarClick(row_0, 'google')} className="w-full cursor-pointer rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none">
                              <span className="flex items-center gap-2">
                                <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5 text-blue-600" fill="currentColor">
                                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                                <span>Google Calendar</span>
                              </span>
                            </button>
                            <button type="button" onClick={() => handleCalendarClick(row_0, 'office365')} className="w-full cursor-pointer rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none">
                              <span className="flex items-center gap-2">
                                <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5 text-blue-700" fill="currentColor">
                                  <path d="M20 3H6c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14H7v-2h4v2zm0-4H7v-2h4v2zm0-4H7V7h4v2zm8 8h-6v-2h6v2zm0-4h-6v-2h6v2zm0-4h-6V7h6v2z" fill="#0078D4" />
                                </svg>
                                <span>Office 365</span>
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>)}
              </div>
            </div>}
        </div>

        {/* DESCRIPTION with More/Less */}
        {description && <div className="2xl:mb-8 lg:mb-7 mb-6 lg:border-b lg:border-[#D9D9D9] 2xl:pb-7 xl:pb-6 lg:pb-5">
            <div id="event-desc" className="text-[#626262] font-outfit lg:text-lg text-base font-normal leading-[1.5em]">
              <div dangerouslySetInnerHTML={{
            __html: enableExpandDescription && (expanded || !isTruncated) ? description : enableExpandDescription ? `${short}…` : description
          }} className={`${Styles.ckeditorBlogs} max-w-none inline
                    [&_p]:text-[#626262] [&_p]:leading-[1.5em] [&_p]:mb-4 [&_p]:inline
                    [&_ul]:list-disc [&_ul]:list-outside 2xl:[&_ul]:pl-10 xl:[&_ul]:pl-8 [&_ul]:pl-6 lg:[&_ul]:mt-4 [&_ul]:mt-3 
                    [&_ol]:list-decimal [&_ol]:list-inside 2xl:[&_ol]:pl-8 xl:[&_ol]:pl-5 [&_ol]:pl-3 lg:[&_ol]:mt-4 [&_ol]:mt-3 
                    lg:[&_li]:my-2 [&_li]:my-1.5 [&_li]:text-[#626262] 
                    [&_ul>li::marker]:text-[#00EAFD] [&_ol>li::marker]:text-[#626262] 
                    [&_strong]:text-[#323232] 
                    [&_a]:underline [&_a:hover]:no-underline 
                    [&_a.btn]:no-underline [&_a.btn]:inline-flex [&_a.btn]:items-center [&_a.btn]:justify-center 
                    [&_a.btn]:rounded-full [&_a.btn]:px-6 [&_a.btn]:py-2.5 [&_a.btn]:font-semibold [&_a.btn]:shadow-sm 
                    [&_a.btn.btn-primary]:bg-[#00EAFD] [&_a.btn.btn-primary]:text-[#2B2B2B]
                  `} />
              {enableExpandDescription && isTruncated && <button type="button" aria-expanded={expanded} aria-controls="event-desc" aria-label={expanded ? 'Collapse to hide additional content' : 'Expand to read more content'} onClick={() => setExpanded(v => !v)} className="inline items-center cursor-pointer gap-1 lg:text-lg text-base font-medium text-[#626262] hover:underline focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-sky-600">
                  {expanded ? 'less' : 'more'}
                </button>}
            </div>
          </div>}

        <div className="flex gap-4">
          {isValidUrl(directionsUrl) && <div className="flex-1 sm:flex-none">
              <UIButton variant="gradient" href={directionsUrl} target="_blank" className="w-full !max-w-full sm:w-auto sm:!max-w-max" {...externalLinkProps(directionsUrl)}>
                <span className="inline-flex">Directions</span>
                {iconLocation && <Image src={iconLocation} alt="Location" width={18} height={18} priority />}
              </UIButton>
            </div>}

          {isValidUrl(detailsUrl) && <div className="flex-1 sm:flex-none">
              <UIButton variant="gradient" href={detailsUrl} target="_blank" className="w-full !max-w-full sm:w-auto sm:!max-w-max" {...externalLinkProps(detailsUrl)} aria-label={`Details about ${title}`}>
                <span className="inline-flex">Details</span>
                {iconOpenInNew && <Image src={iconOpenInNew} alt="Open In New" width={18} height={18} priority />}
              </UIButton>
            </div>}
        </div>
      </div>
    </div>;
}